import React, { useState, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Grid, Button, CircularProgress
} from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { variables } from '../Variables';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/en';

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.locale('en');

const defaultCategory = {
    categoryName: ''
};

const defaultFilter = {
    PageNo: 1,
    PageSize: 10,
    OrderBy: '',
    OrderType: '',
    CategoryName: '',
    CategoryId: null,
    CreatedOn: null,
    UpdatedOn: null
};

const MAX_IMAGE_SIZE_MB = 2;

// Builds a browsable URL for an image path stored in the DB (e.g. "categories/xyz.png").
// variables.projectImgPath holds the static-file host root, e.g.
// "https://localhost:7100" in dev or "https://api.nexusprimeind.com" in production.
const getImageUrl = (path) => {
    if (!path) return null;
    const separator = path.startsWith('/') ? '' : '/';
    return `${variables.projectImgPath}${separator}categories${separator}${path}`;
};

export const Categories = ({ showMessage }) => {
    const user = useSelector((state) => state.user.user);
    const jsonconfig = {
        headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json'
        }
    };

    // Do NOT set Content-Type manually for multipart requests - axios/the browser
    // needs to generate the correct multipart boundary itself.
    const formDataConfig = {
        headers: {
            Authorization: `Bearer ${user.token}`
        }
    };

    const [category, setCategory] = useState(defaultCategory);
    const [editMode, setEditMode] = useState(false);
    const [editCategoryId, setEditCategoryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(defaultFilter);
    const [categoriesList, setCategoriesList] = useState([]);
    const [totalCategories, setTotalCategories] = useState(0);
    const [errors, setErrors] = useState({});
    const [categoryStatus, setCategoryStatus] = useState(null);

    // --- image upload state ---
    const [categoryImage, setCategoryImage] = useState(null);        // newly selected File
    const [imagePreview, setImagePreview] = useState(null);          // object URL for the newly selected File
    const [existingImagePath, setExistingImagePath] = useState(null); // DB path when editing

    const fileInputRef = useRef(null);

    // Revoke the object URL when it changes/unmounts to avoid memory leaks
    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const validate = () => {
        const validationErrors = {};

        if (!category.categoryName || category.categoryName.trim() === '') {
            validationErrors.categoryName = 'Category Name is required.';
        }

        // Image is only mandatory when creating a new category.
        // On edit, keep the existing image if the user doesn't pick a new one.
        if (!editMode && !categoryImage) {
            validationErrors.categoryImage = 'Category Image is required.';
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const resetForm = () => {
        setCategory(defaultCategory);
        setEditMode(false);
        setEditCategoryId(null);
        setErrors({});
        setCategoryImage(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
        setExistingImagePath(null);
        setCategoryStatus(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const handleFieldChange = (field, value) => {
        setCategory(prev => ({ ...prev, [field]: value }));

        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'image/png') {
            showMessage('error', 'Only PNG images are allowed.');
            e.target.value = null;
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            showMessage('error', `Image size should not exceed ${MAX_IMAGE_SIZE_MB}MB.`);
            e.target.value = null;
            return;
        }

        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setCategoryImage(file);
        setImagePreview(URL.createObjectURL(file));

        if (errors.categoryImage) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.categoryImage;
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const url = variables.API_URL + `ClothingCategory/${editMode ? 'UpdateCategory' : 'CreateCategory'}`;

            const formData = new FormData();
            formData.append('CategoryName', category.categoryName);
            formData.append('CreatedBy', user.user.userId);

            if (editMode) {
                formData.append('CategoryId', editCategoryId);
                formData.append('IsActive', categoryStatus === 'true');
            }

            if (categoryImage) {
                formData.append('Image', categoryImage);
            }

            await axios.post(url, formData, formDataConfig);
            showMessage('success', `Category ${editMode ? 'updated' : 'created'} successfully.`);
            resetForm();
            loadCategories();
        } catch (error) {
            const data = error.response?.data;
            let message = "Something went wrong.";

            if (typeof data === 'string') {
                message = data;
            } else if (data?.error) {
                message = data.error;               // your own catch-block shape
            } else if (data?.errors) {
                // ASP.NET Core ValidationProblemDetails
                message = Object.values(data.errors).flat().join(' ');
            } else if (data?.title) {
                // Generic ProblemDetails
                message = `${data.title}${data.status ? ` (status ${data.status})` : ''}`;
            } else if (data) {
                message = JSON.stringify(data);
            }

            showMessage('error', message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${variables.API_URL}ClothingCategory/DeleteCategory?categoryId=${id}`,
                {},
                jsonconfig
            );

            showMessage('success', response?.data);
            loadCategories();
        } catch (error) {
            const errorMsg = error.response?.data || "Something went wrong while deleting.";
            showMessage("error", errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (row) => {
        setEditMode(true);
        setEditCategoryId(row.categoryId);
        setCategoryStatus(row.isActive ? 'true' : 'false'); // normalize boolean -> string to match <select> values
        setCategory({ ...row });
        setCategoryImage(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
        setExistingImagePath(row.imagePath || null);
        setErrors({});
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const columns = [
        { field: 'categoryId', headerName: 'ID', width: 50 },
        { field: 'categoryName', headerName: 'Name', width: 150 },
        {
            field: 'imagePath',
            headerName: 'Image',
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                params.value ? (
                    <img
                        src={getImageUrl(params.value)}
                        alt={params.row.categoryName}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                    />
                ) : (
                    <span className="text-muted">—</span>
                )
            )
        },
        {
            field: 'createdOn', headerName: 'Created On',
            valueFormatter: (params) =>
                dayjs(params.value).format('ddd-MMM-YYYY HH:mm'),
            width: 200
        },
        {
            field: 'updatedOn', headerName: 'Updated On',
            valueFormatter: (params) =>
                dayjs(params.value).format('ddd-MMM-YYYY HH:mm'),
            width: 200
        },
        { field: 'isActive', headerName: 'Visibility', width: 190, disableColumnMenu: true },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(params.row.categoryId)}
                        style={{ marginRight: 8 }}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params.row)}
                    >
                        Edit
                    </Button>
                </>
            )
        }
    ];

    const loadCategories = async () => {
        setLoading(true);
        try {
            const res = await axios.post(
                variables.API_URL + 'ClothingCategory/GetAllCategoriesForAdmin',
                filter,
                jsonconfig
            );
            setCategoriesList(res.data.categories || []);
            setTotalCategories(res.data.totalCount || 0);
        } catch (error) {
            showMessage('error', 'Failed to load categories.');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterModel) => {
        const newFilter = {
            ...filter,
            CategoryName: '',
            CategoryId: null,
            CreatedOn: null,
            UpdatedOn: null,
            PageNo: 1
        };

        const activeFilters = filterModel.items.filter(
            (item) => item.value !== undefined && item.value !== null && item.value !== ''
        );

        activeFilters.forEach(({ field, value }) => {
            switch (field) {
                case 'categoryName':
                    newFilter.CategoryName = value.trim?.() || value;
                    break;
                case 'categoryId':
                    newFilter.CategoryId = parseInt(value);
                    break;
                case 'createdOn':
                    newFilter.CreatedOn = value;
                    break;
                case 'updatedOn':
                    newFilter.UpdatedOn = value;
                    break;
            }
        });

        if (JSON.stringify(filter) !== JSON.stringify(newFilter)) {
            setFilter(newFilter);
        }
    };

    useEffect(() => {
        loadCategories();
    }, [filter.PageNo, filter.PageSize, filter.CategoryId, filter.OrderBy, filter.OrderType, filter.CategoryName, filter.CreatedOn, filter.UpdatedOn]);

    return (
        <>
            <div className="row">
                <div className="col-sm-12 px-4">
                    <div className="form-container mb-3">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                <ul className="text-start list-style-none mb-0">
                                    {Object.values(errors).map((err, idx) => (
                                        <li key={idx}>{idx + 1}. {err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">Category Name</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <input
                                        type="text"
                                        id="name"
                                        className={`form-control ${errors.categoryName ? 'border-red' : ''}`}
                                        value={category.categoryName}
                                        onChange={(e) => handleFieldChange('categoryName', e.target.value)}
                                    />
                                </div>

                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">Category Image (PNG only)</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept=".png,image/png"
                                        className={`form-control ${errors.categoryImage ? 'border-red' : ''}`}
                                        onChange={handleImageChange}
                                    />
                                    {(imagePreview || existingImagePath) && (
                                        <div className="mt-2">
                                            <img
                                                src={imagePreview || getImageUrl(existingImagePath)}
                                                alt="preview"
                                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                                            />
                                            {editMode && !categoryImage && (
                                                <div className="small text-muted">Current image (select a new PNG to replace it)</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {editMode && (
                                    <>
                                        <label className="col-sm-4 col-lg-2 form-group-label mb-3">Status</label>
                                        <div className="col-sm-8 col-lg-4 mb-3">
                                            <select className="form-select" value={categoryStatus ?? ''} onChange={(e) => setCategoryStatus(e.target.value)}>
                                                <option value="">Select Status</option>
                                                <option value="true">Active</option>
                                                <option value="false">InActive</option>
                                            </select>
                                        </div>
                                    </>
                                )}

                                <div className="col-12 text-end">
                                    <button type="submit" className="site-btn" disabled={loading}>
                                        {loading ? 'Saving...' : editMode ? 'Update Category' : 'Upload Category'}
                                    </button>
                                    {editMode && (
                                        <button type="button" className="site-btn ms-2" onClick={resetForm}>
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12 px-4">
                    <span className="form-title">Categories List</span>
                    <div className="form-container mb-3" style={{ height: 500, width: '100%' }}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <DataGrid
                                rows={categoriesList}
                                columns={columns}
                                getRowId={(row) => row.categoryId}
                                rowCount={totalCategories}
                                loading={loading}
                                paginationMode="server"
                                sortingMode="server"
                                filterMode="server"
                                onFilterModelChange={handleFilterChange}
                                paginationModel={{
                                    page: filter.PageNo - 1,
                                    pageSize: filter.PageSize,
                                }}
                                onPaginationModelChange={({ page, pageSize }) => {
                                    setFilter(prev => ({
                                        ...prev,
                                        PageNo: page + 1,
                                        PageSize: pageSize,
                                    }));
                                }}
                                sortModel={
                                    filter.OrderBy && filter.OrderType
                                        ? [{ field: filter.OrderBy, sort: filter.OrderType }]
                                        : []
                                }
                                onSortModelChange={(sortModel) => {
                                    if (sortModel.length > 0 && (sortModel[0].sort === 'asc' || sortModel[0].sort === 'desc')) {
                                        const { field, sort } = sortModel[0];
                                        setFilter(prev => ({
                                            ...prev,
                                            OrderBy: field,
                                            OrderType: sort
                                        }));
                                    } else {
                                        setFilter(prev => ({
                                            ...prev,
                                            OrderBy: '',
                                            OrderType: ''
                                        }));
                                    }
                                }}
                                pageSizeOptions={[10, 20, 50]}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};